/* eslint-disable no-param-reassign */
import {IconButton} from '@dropbox/dig-components/buttons';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddMediaLine,
  BoldLine,
  HeaderLine,
  ItalicLine,
  LinkLine,
  OrderedListLine,
  UnorderedListLine,
} from '@dropbox/dig-icons/assets';
import {$isLinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
} from '@lexical/list';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$isHeadingNode} from '@lexical/rich-text';
import {$setBlocksType} from '@lexical/selection';
import {$getNearestNodeOfType, mergeRegister} from '@lexical/utils';
import {snackbarAtom} from 'atoms/snackbar';
import {ACCEPTABLE_FILE_TYPES, handleFiles} from 'components/DSYS/editor/nodes/fileUploadUtil';
import {
  RICH_TEXT_MEDIA_UPLOAD_SIZE_LIMIT_BYTES,
  useRichTextMediaUpload,
} from 'components/DSYS/editor/plugins/MediaUploadContext';
import {getSelectedNode, LowPriority} from 'components/DSYS/editor/utils';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {$createEnhancedHeadingNode} from 'plugins/enhancedHeadings/EnhancedHeadingNode';
import {useCallback, useEffect, useRef, useState} from 'react';

export const RichTextToolbar = ({
  top,
  media,
  disabled = false,
  disableStickyToolbar,
  showHeadingControl = Boolean(media),
}: {
  top: number;
  media?: boolean;
  disabled?: boolean;
  disableStickyToolbar?: boolean;
  showHeadingControl?: boolean;
}) => {
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [editor] = useLexicalComposerContext();
  const mediaUpload = useRichTextMediaUpload();
  const maxFileSizeBytes = mediaUpload.maxFileSizeBytes ?? RICH_TEXT_MEDIA_UPLOAD_SIZE_LIMIT_BYTES;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [blockType, setBlockType] = useState('paragraph');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);

  const disabledColor = 'Disabled Base';

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const validFiles = Array.from(event.target.files).filter(
        (file) => file.size <= maxFileSizeBytes
      );

      if (validFiles.length !== event.target.files.length) {
        setSnackbarMessage({
          text: t('rich_text_upload_size_error'),
        });
      }

      handleFiles([...validFiles], mediaUpload, editor);
      // Reset file input
      event.target.value = '';
    },
    [editor, maxFileSizeBytes, mediaUpload, setSnackbarMessage]
  );

  // Highlight toolbar items based on what's selected
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElement();
      if (!element) return;
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          setBlockType(type);
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
        url: '',
        target: '_blank',
        rel: 'noopener noreferrer',
      });
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const insertMedia = useCallback(() => fileInputRef.current?.click(), []);
  const preserveEditorFocus = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault();
  };

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = () => {
    if (!/h\d$/.test(blockType)) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createEnhancedHeadingNode('h1'));
        }
      });
    } else {
      formatParagraph();
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      // Use $setBlocksType to only convert selected items to paragraphs,
      // not the entire list
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      // Use $setBlocksType to only convert selected items to paragraphs,
      // not the entire list
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  };

  return (
    <Box
      id="rich-text-toolbar"
      position={disableStickyToolbar ? 'relative' : 'sticky'}
      style={{top: disableStickyToolbar ? 0 : top, zIndex: 1}}
    >
      <Box backgroundColor="Background Base" borderRadius="Small">
        <Split padding="4" gap="6" paddingBottom="8" alignY="center">
          {showHeadingControl && (
            <Split.Item>
              <IconButton
                type="button"
                variant="borderless"
                disabled={disabled}
                onMouseDown={preserveEditorFocus}
                onPointerDown={preserveEditorFocus}
                onClick={formatHeading}
              >
                <UIIcon
                  src={HeaderLine}
                  className={atoms({
                    color: disabled
                      ? disabledColor
                      : /h\d$/.test(blockType)
                        ? 'Text Base'
                        : 'Text Subtle',
                  })}
                />
              </IconButton>
            </Split.Item>
          )}
          <Split.Item>
            <IconButton
              type="button"
              variant="borderless"
              disabled={disabled || /h\d$/.test(blockType)}
              onMouseDown={preserveEditorFocus}
              onPointerDown={preserveEditorFocus}
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
            >
              <UIIcon
                src={BoldLine}
                className={atoms({
                  color:
                    disabled || /h\d$/.test(blockType)
                      ? disabledColor
                      : isBold
                        ? 'Text Base'
                        : 'Text Subtle',
                })}
              />
            </IconButton>
          </Split.Item>
          <Split.Item>
            <IconButton
              type="button"
              variant="borderless"
              disabled={disabled || /h\d$/.test(blockType)}
              onMouseDown={preserveEditorFocus}
              onPointerDown={preserveEditorFocus}
              onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
            >
              <UIIcon
                src={ItalicLine}
                className={atoms({
                  color:
                    disabled || /h\d$/.test(blockType)
                      ? disabledColor
                      : isItalic
                        ? 'Text Base'
                        : 'Text Subtle',
                })}
              />
            </IconButton>
          </Split.Item>
          <Split.Item>
            <IconButton
              type="button"
              variant="borderless"
              disabled={disabled}
              onMouseDown={preserveEditorFocus}
              onPointerDown={preserveEditorFocus}
              onClick={formatBulletList}
            >
              <UIIcon
                src={UnorderedListLine}
                className={atoms({
                  color: disabled
                    ? disabledColor
                    : blockType === 'ul'
                      ? 'Text Base'
                      : 'Text Subtle',
                })}
              />
            </IconButton>
          </Split.Item>
          <Split.Item>
            <IconButton
              type="button"
              variant="borderless"
              disabled={disabled}
              onMouseDown={preserveEditorFocus}
              onPointerDown={preserveEditorFocus}
              onClick={formatNumberedList}
            >
              <UIIcon
                src={OrderedListLine}
                className={atoms({
                  color: disabled
                    ? disabledColor
                    : blockType === 'ol'
                      ? 'Text Base'
                      : 'Text Subtle',
                })}
              />
            </IconButton>
          </Split.Item>

          <Split.Item>
            <Box borderLeft="Solid" marginTop="6" style={{height: 20}} />
          </Split.Item>

          <Split.Item>
            <IconButton
              type="button"
              variant="borderless"
              disabled={disabled}
              onMouseDown={preserveEditorFocus}
              onPointerDown={preserveEditorFocus}
              onClick={insertLink}
            >
              <UIIcon
                src={LinkLine}
                className={atoms({
                  color: disabled ? disabledColor : isLink ? 'Text Base' : 'Text Subtle',
                })}
              />
            </IconButton>
          </Split.Item>

          {media && (
            <Split.Item>
              <IconButton
                type="button"
                variant="borderless"
                disabled={disabled}
                onMouseDown={preserveEditorFocus}
                onPointerDown={preserveEditorFocus}
                onClick={insertMedia}
              >
                <UIIcon
                  src={AddMediaLine}
                  className={atoms({color: disabled ? disabledColor : 'Text Subtle'})}
                />
              </IconButton>
            </Split.Item>
          )}
        </Split>
      </Box>
      <input
        type="file"
        ref={fileInputRef}
        style={{display: 'none'}}
        accept={ACCEPTABLE_FILE_TYPES.join(',')}
        disabled={disabled}
        onChange={handleFileChange}
      />
    </Box>
  );
};
